/**
 * Kanban Board Component
 * Main drag-and-drop board for task management
 */

import { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  CollisionDetection,
} from '@dnd-kit/core';
import { Loader2 } from 'lucide-react';
import { KanbanColumn } from './KanbanColumn';
import { TaskCard } from './TaskCard';
import { TaskDetailModal } from './TaskDetailModal';
import { useKanbanTasks, useUpdateTaskPositions } from '../hooks/useTasks';
import type { KanbanQueryParams, TaskListItem, TaskStatus } from '../types';
import { TASK_STATUS_ORDER } from '../types';
import type { QuoteOption } from '../api';

interface KanbanBoardProps {
  filters: KanbanQueryParams;
  quotes?: QuoteOption[];
  assignees?: Array<{ id: string; name: string; email?: string }>;
  canEditTasks?: boolean;
  canDeleteTasks?: boolean;
  canAssignTasks?: boolean;
}

export function KanbanBoard({
  filters,
  quotes = [],
  assignees = [],
  canEditTasks = true,
  canDeleteTasks = false,
  canAssignTasks = false,
}: KanbanBoardProps) {
  const { data, isLoading, error } = useKanbanTasks(filters);
  const updatePositions = useUpdateTaskPositions();

  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskListItem | null>(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    })
  );

  // Custom collision detection that prioritizes pointerWithin for better column detection
  // Falls back to rectIntersection when pointer is not directly over a droppable
  const collisionDetection: CollisionDetection = useMemo(
    () => (args) => {
      // First check pointer within (more precise for columns)
      const pointerCollisions = pointerWithin(args);
      if (pointerCollisions.length > 0) {
        return pointerCollisions;
      }
      // Fallback to rect intersection for edge cases
      return rectIntersection(args);
    },
    []
  );

  // Find the active task being dragged
  const activeTask = activeTaskId
    ? data?.columns.flatMap((col) => col.tasks).find((task) => task.id === activeTaskId)
    : null;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (!canEditTasks) return;
    setActiveTaskId(event.active.id as string);
  }, [canEditTasks]);

  const handleDragOver = useCallback((_event: DragOverEvent) => {
    // Optional: Visual feedback while dragging over columns
    // The column highlighting is handled by useDroppable in KanbanColumn
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveTaskId(null);

      if (!canEditTasks) return;
      if (!over || !data) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      // Find source task and its column
      let sourceColumn: (typeof data.columns)[0] | undefined;
      let sourceTask: TaskListItem | undefined;

      for (const col of data.columns) {
        const task = col.tasks.find((t) => t.id === activeId);
        if (task) {
          sourceColumn = col;
          sourceTask = task;
          break;
        }
      }

      if (!sourceColumn || !sourceTask) return;

      // Determine target column and position
      let targetStatus: TaskStatus | undefined;
      let targetPosition: number | undefined;

      // Check if dropping on a column or a task
      const targetColumn = data.columns.find((col) => col.status === overId);
      if (targetColumn) {
        // Dropping on a column - add to end
        targetStatus = targetColumn.status;
        // Filter out invalid positions and calculate max safely
        const validPositions = targetColumn.tasks
          .map((t) => t.position)
          .filter((p) => typeof p === 'number' && !isNaN(p));
        targetPosition =
          validPositions.length > 0
            ? Math.max(...validPositions) + 1000
            : 1000;
      } else {
        // Dropping on a task - insert before it
        for (const col of data.columns) {
          const overTaskIndex = col.tasks.findIndex((t) => t.id === overId);
          if (overTaskIndex !== -1) {
            const overTask = col.tasks[overTaskIndex];
            targetStatus = col.status;

            // Calculate insertion position between previous task and overTask
            if (overTaskIndex > 0) {
              const prevTask = col.tasks[overTaskIndex - 1];
              // Insert between previous and current task
              targetPosition = Math.floor((prevTask.position + overTask.position) / 2);
              // If positions are adjacent (no room between), use overTask position - 1
              if (targetPosition <= prevTask.position) {
                targetPosition = overTask.position - 1;
              }
            } else {
              // First position - insert before
              targetPosition = Math.max(0, overTask.position - 1000);
            }
            break;
          }
        }
      }

      // If target not found, no-op
      if (targetStatus === undefined || targetPosition === undefined) return;

      // Check if there's actually a change
      const statusChanged = sourceColumn.status !== targetStatus;
      const positionChanged = sourceTask.position !== targetPosition;

      if (!statusChanged && !positionChanged) return;

      // Build updates array
      const updates: Array<{ id: string; status?: TaskStatus; position: number }> = [];

      if (statusChanged) {
        // Moving to different column
        updates.push({
          id: activeId,
          status: targetStatus,
          position: targetPosition,
        });
      } else {
        // Reordering within same column
        updates.push({
          id: activeId,
          position: targetPosition,
        });
      }

      // Call mutation with optimistic update
      updatePositions.mutate({ updates });
    },
    [canEditTasks, data, updatePositions]
  );

  const handleTaskClick = useCallback((task: TaskListItem) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive">
        Failed to load tasks. Please try again.
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Sort columns by status order
  const sortedColumns = [...data.columns].sort(
    (a, b) => TASK_STATUS_ORDER.indexOf(a.status) - TASK_STATUS_ORDER.indexOf(b.status)
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4 min-h-[500px]">
          {sortedColumns.map((column) => (
            <KanbanColumn
              key={column.status}
              column={column}
              onTaskClick={handleTaskClick}
              activeTaskId={activeTaskId}
              quotes={quotes}
            />
          ))}
        </div>

        {/* Drag Overlay - shows the card being dragged */}
        <DragOverlay>
          {activeTask && <TaskCard task={activeTask} isDragging quotes={quotes} />}
        </DragOverlay>
      </DndContext>

      {/* Task Detail Modal */}
      <TaskDetailModal
        open={showTaskDetail}
        onOpenChange={setShowTaskDetail}
        taskId={selectedTask?.id}
        taskPreview={selectedTask ?? undefined}
        quotes={quotes}
        assignees={assignees}
        canEditTasks={canEditTasks}
        canDeleteTasks={canDeleteTasks}
        canAssignTasks={canAssignTasks}
      />
    </>
  );
}
